import i18next, { type InitOptions, type i18n as I18n } from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { logger } from "@jslib/shared/utils/logger";

import { getMessagesModuleByLocale } from "@/lib/intl/intl";
import { type Locale, type SegmentSpecificMessages } from "@/lib/types/types";
import { type DeepPartial, type StringValueOf } from "@/lib/types/helpers";
import { deepMerge } from "@/lib/utils/objects";

export const Namespace = {
  DEFAULT: "default",
  THEME: "theme",
  OPTIONS: "options",
} as const;

export type Namespaces = StringValueOf<typeof Namespace>;

/**
 * TODO: Move this into the form render so that we can report this out to NR along with other
 * dimentions such as program ID, verification ID, etc.
 */
i18next.on("missingKey", (lngs, namespace, key, res) => {
  // eslint-disable-next-line no-console
  logger.warn(`i18next: MISSING KEY in ${namespace}.${key} for languages: ${lngs.join(", ")}`, {
    result: res,
  });
});

/**
 * TODO: This function can be elimitated once we migrate away from React-Intl and can
 * update the messages keys directly in the message bundles.
 *  */
export function contextualizeMessageKey(
  context: string,
  segmentMessages: DeepPartial<SegmentSpecificMessages>,
) {
  if (typeof segmentMessages !== "object") return undefined;

  const messages = {};

  Object.entries(segmentMessages).forEach(([key, messageBundle]) => {
    if (typeof messageBundle === "string") messages[`${key}_${context}`] = messageBundle;
    if (typeof messageBundle === "object")
      messages[key] = contextualizeMessageKey(
        context,
        messageBundle as DeepPartial<SegmentSpecificMessages>,
      );
  });

  return messages;
}

async function resourcesCallback(lng: Locale, ns: Namespaces) {
  const localizedMessages = await getMessagesModuleByLocale(lng as any);

  let messages;
  switch (ns) {
    case "default": {
      const segmentMessageEntries = Object.entries(localizedMessages.segmentMessages);
      const contextMessages = segmentMessageEntries.map(([segment, segmentMessages]) =>
        contextualizeMessageKey(segment, segmentMessages),
      );

      messages = deepMerge({}, localizedMessages.defaultMessages, ...contextMessages);

      break;
    }
    case "theme":
      // TODO: Loading the Program Messaging from an independent Localization Service.
      break;
    case "options": {
      // Currently loaded by a call to setOptions, using the `messagesWithLocale` as reference.
      break;
    }
    default:
      // This 'satisfies never' ensures that all possible namespaces are handled.
      logger.error("Unexpected namespace resource request", ns satisfies never);
  }

  return messages;
}

const i18nextOptions: InitOptions = {
  debug: false,
  partialBundledLanguages: true,
  ns: Object.values(Namespace),
  defaultNS: Namespace.OPTIONS,
  fallbackNS: [Namespace.THEME, Namespace.DEFAULT],
  /**
   * Setting a default `lng` can cause some race conditions in rendering causing theme override
   * messages to not be displayed; we address this by deferring the load to the
   * createFetchVerificationRequest action, around the same time as react-intl is being reloaded
   * When the property is not set i18next defaults to `cimode` as the lng which will show the
   * message keys instead of translations
   */
  // lng: "en-US",
  /***
   * By default, i18next will try each namespace in each language before moving to the next namespace.
   * e.g., options:fr -> options:en-US -> theme:fr -> theme:en-US -> ...
   * This can cause an issue the same key is present in a higher priority namespace of the fallback,
   * lanugage as is the case with keys like `step.personalInfo.subtitle`; often found in the `en-US`
   * theme bundle. Disabling fallbackLng forces i18next to exhaust all 'fr' namespaces first.
   */
  fallbackLng: false,
  react: {
    transSupportBasicHtmlNodes: false,
  },
  showSupportNotice: false,
};

// Maintain visual sytle with the createI18n function.
// prettier-ignore
i18next
  .use(initReactI18next)
  .use(resourcesToBackend(resourcesCallback))
  .init(i18nextOptions);

export function addOptionsMessages(
  messagesWithLocale: { [key: string]: object },
  i18nextInstance: I18n = i18next,
) {
  if (!messagesWithLocale) {
    return;
  }
  Object.entries(messagesWithLocale).forEach(([lng, resource]) => {
    if (resource) i18nextInstance.addResourceBundle(lng, Namespace.OPTIONS, resource, false, true);
  });
}

export async function createI18n(overrideOptions: InitOptions = {}): Promise<I18n> {
  // can't use cloneInstance as the resourceToBackend is connected to the primary resource
  // bundle, instead we need to replicate the creation steps.
  const newI18n = i18next.createInstance();
  await newI18n
    .use(initReactI18next)
    .use(resourcesToBackend(resourcesCallback))
    .init({
      ...i18nextOptions,
      ...overrideOptions,
    });
  return newI18n;
}

export default i18next;
